import React, { useCallback, useContext, useMemo } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { intersection, isNil, map, prop } from "ramda";

import { Permission, useGetCurrentUserQuery } from "gql";

import { AuthContextType } from "../types";

export const AuthContext = React.createContext<AuthContextType>([
  { isPermitted: () => false },
  {
    currentUser: null,
    permissions: null,
  },
]);

export const checkPermissions = (permits: string[], permissions?: Maybe<string[]>): boolean =>
  !isNil(permissions) ? intersection(permits, permissions).length > 0 : false;

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { showBoundary: onError } = useErrorBoundary();
  const { data } = useGetCurrentUserQuery({ onError });

  const permissions = useMemo(
    () =>
      data?.currentUser.role?.permissions
        ? map(prop("codename")<Pick<Permission, "codename">>)(data?.currentUser.role?.permissions)
        : null,
    [data]
  );
  const currentUser = data?.currentUser ?? null;

  const isPermitted = useCallback(
    (permits) => {
      return checkPermissions(permits, permissions);
    },
    [permissions]
  );

  const context = useMemo(
    (): AuthContextType => [{ isPermitted }, { currentUser, permissions }],
    [currentUser, permissions, isPermitted]
  );

  return <AuthContext.Provider value={context}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => useContext(AuthContext);
